import { Modal, Spinner } from 'react-bootstrap'
import React, { useEffect, useState } from 'react'
import { useTheme } from '../../context/ThemeContext'
import { useAuth } from '../../modules/auth'
import { deleteFileAttachment, updateCardById } from '../../services/cards'
import { awsFileUploadConfig } from '../../config/awsFileUpload'
import ReactS3Client from 'react-aws-s3-typescript'
import { FileUploadPage } from '../FileUploader/FileUpload'
import { useDispatch, useSelector } from 'react-redux'
import { Dispatch } from '@reduxjs/toolkit'
import { setIsCardUpdated } from '../../store/slice/last-update-slice'

type Props = {
  attachmentModal: boolean
  setAttachmentModal: (value: boolean) => void
  cardDetails: any
  onAttachmentUpdate?: (updatedCard: any) => void
  setCards?: (cards: any) => void
  cards?: any[]
}

const CardAttachmentModal = ({
  attachmentModal,
  setAttachmentModal,
  cardDetails,
  onAttachmentUpdate,
  setCards,
  cards
}: Props): React.JSX.Element => {
  const { theme } = useTheme()
  const { currentUser }: any = useAuth()
  const { id: userId, fullName } = currentUser?.data.user
  const dispatch: Dispatch<any> = useDispatch()
  const isLastUpdated: any = useSelector((state: any) => state.lastUpdateReducer)
  const { isCardUpdated } = isLastUpdated.isCardUpdated

  const [selectedFile, setSelectedFile] = useState<any>([])
  const [fileErrorMsg, setFileErrorMsg] = useState('')
  const [saving, setSaving] = useState(false)

  const s3 = new ReactS3Client({
    ...awsFileUploadConfig,
    dirName: `cards-attachments/${process.env.REACT_APP_ENVIRONMENT}`,
  })

  useEffect(() => {
    const urlLinks: any = []
    if (cardDetails?.file_attachments) {
      for (const file of cardDetails?.file_attachments) {
        for (const [key, value] of Object.entries(file)) {
          const getFileName = (value as string)
            ?.toLowerCase()
            ?.split(`${process.env.REACT_APP_ENVIRONMENT}/`)[1]
          urlLinks.push({ key, value: value, showName: getFileName })
        }
      }
      setSelectedFile(urlLinks)
    }
    return () => setSelectedFile([])
    //eslint-disable-next-line
  }, [cardDetails?.id])

  const selectFileHandler = (fileList: any) => {
    const files = Array.from(fileList)
    const validFiles = files.filter(
      (file: any) =>
        !file.name.toLowerCase().endsWith('.html') &&
        !file.name.toLowerCase().endsWith('.mhtml')
    )
    const invalidFilesExt = files.filter(
      (file: any) =>
        file.name.toLowerCase().endsWith('.html') ||
        file.name.toLowerCase().endsWith('.mhtml')
    )
    if (invalidFilesExt.length > 0) {
      setFileErrorMsg('Does not support this extension')
    }
    setSelectedFile((prevFiles: any) => [...prevFiles, ...validFiles])
  }

  const deleteFileHandler = async (name: string, key: string, value: string) => {
    let files: any
    if (key) {
      files = selectedFile.filter((item: any) => item.key !== key)
    } else {
      files = selectedFile.filter((item: any) => item.name !== name)
    }
    try {
      const fileNameForDelete = value || name
      await s3.deleteFile(fileNameForDelete)
      if (key) {
        await deleteFileAttachment(cardDetails?.id, key, userId, cardDetails?.boardId)
        // Update parent cards state to reflect deletion
        if (setCards && cards) {
          const updatedCard = {
            ...cardDetails,
            file_attachments: cardDetails?.file_attachments?.filter((file: any) => !file[key])
          }
          setCards((prevCards: any) =>
            prevCards.map((card: any) => card.id === updatedCard.id ? updatedCard : card)
          )
        }
        dispatch(setIsCardUpdated({ isCardUpdated: !isCardUpdated }))
      }
      setSelectedFile(files)
    } catch (error) {
      return error
    }
  }

  const handleUploadFiles = async () => {
    const urls = []
    for (let index = 0; index < selectedFile.length; index++) {
      const filename = selectedFile[index].name
      if (filename) {
        const filenameUpdated = filename.substring(0, filename.lastIndexOf('.'))
        try {
          const uploadFile: any = await s3.uploadFile(selectedFile[index], filenameUpdated)
          urls.push(uploadFile.location)
        } catch (error) {
          return error
        }
      }
    }
    return urls
  }

  const handleSaveAttachments = async () => {
    setSaving(true)
    try {
      const fileLinks = await handleUploadFiles()

      if (fileLinks && Array.isArray(fileLinks) && fileLinks.length > 0) {
        const payload = {
          created_by: fullName,
          last_modified_by: fullName,
          board_name: cardDetails?.board_name?.trim(),
          card_name: cardDetails?.card_name?.trim(),
          description: cardDetails?.description || null,
          due_date: cardDetails?.due_date || null,
          due_time: cardDetails?.due_time || null,
          number: cardDetails?.number,
          position: cardDetails?.position,
          priority: cardDetails?.priority || null,
          rag: null,
          status: cardDetails?.status,
          version: 1,
          applicationId: null,
          boardId: cardDetails?.boardId,
          board_column_id: cardDetails?.board_column_id,
          categoryId: null,
          componentId: null,
          customerId: null,
          productId: null,
          typeId: null,
          reporter: fullName,
          new_tagIds: [],
          remove_tagIds: [],
          assignees: cardDetails?.assigneeBoardPersonIds?.map((assignee: any) => ({
            board_person_id: assignee.boardPersonId
          })) || [],
          file_attachments: fileLinks,
          updateReminders: [],
          removeReminders: [],
        }

        const response = await updateCardById(
          payload,
          cardDetails?.id,
          userId,
          cardDetails?.boardId
        )

        const updatedCard = response.data.data.card

        // Update parent cards state with the new attachments
        if (setCards && cards) {
          setCards((prevCards: any) =>
            prevCards.map((card: any) => card.id === updatedCard.id ? { ...card, ...updatedCard } : card)
          )
        }

        if (onAttachmentUpdate) {
          onAttachmentUpdate(updatedCard)
        }

        dispatch(setIsCardUpdated({ isCardUpdated: !isCardUpdated }))
      }

      setAttachmentModal(false)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving attachments:', error)
    } finally {
      setSaving(false)
    }
  }

  const hasNewFiles = selectedFile.some((file: any) => file.name && !file.key)

  return (
    <Modal
      contentClassName={theme === 'dark' ? 'theme__div' : ''}
      show={attachmentModal}
      onHide={() => setAttachmentModal(false)}
      size='lg'
      aria-labelledby='contained-modal-title-vcenter'
      backdrop='static'
    >
      <Modal.Header>
        <Modal.Title
          className={`text-center w-100 fw-bold ${theme === 'dark' ? 'white__text' : ''}`}
        >
          Attachments for {cardDetails?.key}
        </Modal.Title>
        <i
          className={`bi bi-x-lg modal-icon-custom ${theme === 'dark' && 'close'}`}
          onClick={() => setAttachmentModal(false)}
        ></i>
      </Modal.Header>
      <Modal.Body className={theme === 'dark' ? 'dark-mode-placeholder' : ''}>
        <FileUploadPage
          deleteFileHandler={deleteFileHandler}
          selectedFile={selectedFile}
          changeHandler={selectFileHandler}
        />
        {fileErrorMsg && (
          <div className='alert alert-warning mt-2'>{fileErrorMsg}</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          className='btn btn-secondary'
          onClick={() => setAttachmentModal(false)}
        >
          Close
        </button>
        {hasNewFiles && (
          <button
            className='btn btn-primary'
            onClick={handleSaveAttachments}
            disabled={saving}
          >
            {saving ? <Spinner animation='border' size='sm' /> : 'Save Attachments'}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default CardAttachmentModal
